import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  clearBusinessAthlete,
  clearBusinessCacheInformation,
  fetchCacheInformation,
  fetchCacheInformationById,
} from "../../../store/actions/businessActions";
import { clearAthleteProfile, fetchAthleteProfileByEmail } from "../../../store/actions/athleteActions";
import { fetchAnalytics } from "../../../store/actions/userActions";
import { fetchMyCircle } from "../../../store/actions/circleActions";
import { useCodeMaps } from "../../../utils/utils";
import { buildProfileModel } from "../PlayerProfile/profileModel";
import SubscriptionStatusModal from "../../common/SubscriptionStatusModal";
import ProfileBody, { profileSubtitle, watchBrandMatches } from "./ProfileBody";
import { useProfileTabParam } from "./performance/profileTabs";
import { SocialLinks, ValueContextModal } from "./profileSections";
import { ProfileIdentity } from "./mobile/mobileProfileSections";
import { Btn, EmptyState, InsightBox, Loading, PageHeader } from "../../ForBrands/ui/BrandUI";
import { MobileEmpty, MobilePageHead } from "../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";

// The athlete profile coaches, reps, brands, recruiters, AD/PD and staff
// open (Coach, Rep and Brand Mode's athletes/:athleteId and the Player
// Index's /business/athletes/:athleteId). The page body only: the shell the
// route sits in draws the frame. The same V2 sections as the athlete's own
// My Profile (ProfileBody), read-only and in the third person.
//
// The athlete comes from the athleteId prop or the :athleteId param: a
// 24-hex id, an email, or nothing (then the last athlete opened, from
// localStorage "athleteEmail").
//
// Each load is one request for one athlete. When :athleteId changes before
// a load finishes, that load goes stale: its answers are dropped before
// they reach the store, so a late response for the previous athlete can
// never replace the one the URL names.
//
// Inside the phone theme (Coach Mode below 640px, Figma 2000:373 to
// 2000:740) the profile opens as the athlete's own does on a phone: five
// tabs, with the identity row and a full-width Back at the top of Overview
// and no PageHeader. Rep, Brand and the Player Index keep this layout on
// phones (their shells have no phone theme).
//
// The desktop page's tabs under the rate card (Performance, Visibility,
// Value) keep the open one in ?tab=, the phone tabs' key
// (useProfileTabParam).

// Tiers that unlock CacheValue, CacheNILx and the NILx rate card for a
// viewer (admins always pass the Paywall).
export const VIEWER_PREMIUM = ["athlete_premium", "coach_core", "coach_pro", "coach_premium"];

// Roles that get the subscription status prompt once the profile loads.
const SUBSCRIPTION_PROMPT_ROLES = ["IS_BRAND_AGENCY", "IS_AD_PD", "IS_COACH", "IS_RECRUITER_SCOUT"];

const OBJECT_ID = /^[a-f0-9]{24}$/;
const NOT_COMPLETED = "Athlete Profile Information is not completed.";
const SCORE_PENDING = "CacheScore is not calculated.";
const EMPTY = {};

// The eyebrow names the shell the URL is in.
const SHELL_EYEBROWS = [
  ["/coach", "Coach Mode"],
  ["/rep", "Rep Mode"],
  ["/brand", "Brand Mode"],
];

export const eyebrowFor = (pathname = "") => {
  const hit = SHELL_EYEBROWS.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return hit ? hit[1] : "Player Index";
};

const permissionsFrom = (token) => {
  try {
    return (token ? jwtDecode(token) : {}).permissions || [];
  } catch {
    return [];
  }
};

const AthleteProfileView = ({ athleteId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();
  const param = athleteId || params.athleteId;
  const athlete = useSelector((state) => state.athlete?.data || EMPTY);
  const cacheInformation = useSelector((state) => state.business?.cacheInformation || EMPTY);
  const codes = useSelector((state) => state.codes?.allCodes);
  const token = useSelector((state) => state.auth?.token);
  const entitlement = useSelector((state) => state.entitlement?.entitlement);
  const codeMap = useCodeMaps(codes);
  // Read when a load finishes (the subscription prompt), so a new token
  // does not reload the athlete on screen.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const [loading, setLoading] = useState(true);
  const [circle, setCircle] = useState([]);
  // null = still matching, [] = none / unavailable.
  const [matches, setMatches] = useState(null);
  const [contextOpen, setContextOpen] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const closeSubscription = useCallback(() => setShowSubscription(false), []);
  const phone = usePhoneTheme();
  const [tab, setTab] = useProfileTabParam();

  useEffect(() => {
    const isObjectId = !!param && OBJECT_ID.test(param);
    const email = !param ? localStorage.getItem("athleteEmail") : !isObjectId ? param : null;
    let cancelled = false;
    // Runs a fetch thunk with a dispatch that ignores it once this load is
    // stale, so only the newest athlete's responses land in the store.
    const guarded = (thunk) => (storeDispatch, getState, extra) =>
      thunk((action) => (cancelled ? undefined : storeDispatch(action)), getState, extra);

    // The athlete doc is cleared too, so a failed load reads "not found"
    // instead of the athlete opened before.
    dispatch(clearBusinessAthlete());
    dispatch(clearBusinessCacheInformation());
    dispatch(clearAthleteProfile());
    setLoading(true);
    setCircle([]);
    setContextOpen(false);

    if (!param && !email) {
      setLoading(false);
      return undefined;
    }

    const onDone = () => {
      if (cancelled) return;
      setLoading(false);
      const permissions = permissionsFrom(tokenRef.current);
      if (SUBSCRIPTION_PROMPT_ROLES.some((p) => permissions.includes(p))) setShowSubscription(true);
    };
    const loadCircle = (address) =>
      dispatch(guarded(fetchMyCircle(address)))
        .then((members) => !cancelled && setCircle(members || []))
        .catch(() => !cancelled && setCircle([]));

    if (isObjectId) {
      dispatch(guarded(fetchCacheInformationById(param)))
        .then((data) => {
          if (cancelled || !data?.athleteEmail) return undefined;
          localStorage.setItem("athleteEmail", data.athleteEmail);
          loadCircle(data.athleteEmail);
          return dispatch(guarded(fetchAthleteProfileByEmail(data.athleteEmail)));
        })
        .finally(onDone);
    } else {
      loadCircle(email);
      Promise.all([dispatch(guarded(fetchAthleteProfileByEmail(email))), dispatch(guarded(fetchCacheInformation(email)))]).finally(onDone);
    }

    dispatch(fetchAnalytics());
    return () => {
      cancelled = true;
    };
  }, [dispatch, param]);

  const model = useMemo(
    () => buildProfileModel(athlete, cacheInformation, codeMap, circle, { selfView: false }),
    [athlete, cacheInformation, codeMap, circle],
  );

  const onlyMessage = Object.keys(cacheInformation).length === 1 && cacheInformation.message;
  const notCompleted = onlyMessage === NOT_COMPLETED;
  const scorePending = onlyMessage === SCORE_PENDING;
  // Brand matches load once the full profile shows, as before.
  const readyEmail = !loading && athlete.email && !notCompleted && !scorePending ? athlete.email : null;

  useEffect(() => {
    if (!readyEmail) return undefined;
    return watchBrandMatches(readyEmail, setMatches);
  }, [readyEmail]);

  const eyebrow = eyebrowFor(pathname);
  const back = (
    <Btn variant="secondary" onClick={() => navigate(-1)}>
      Back
    </Btn>
  );

  if (loading) return <Loading label="Loading profile..." />;

  if (phone) {
    // flex-1 fills the identity row's action row; the page head stacks its
    // actions in a column, where flex-1 would collapse the 52px height, so
    // the Back there is simply full width.
    const phoneBackBtn = (className) => (
      <Btn variant="secondary" size="lg" className={className} onClick={() => navigate(-1)}>
        Back
      </Btn>
    );
    const phoneBack = phoneBackBtn("min-w-0 flex-1 !px-3");
    if (!athlete.email) {
      return (
        <div className="flex flex-col gap-[18px]">
          <MobilePageHead title="Athlete not found">{phoneBackBtn("w-full")}</MobilePageHead>
          <MobileEmpty>We couldn't locate this athlete's profile.</MobileEmpty>
        </div>
      );
    }
    if (notCompleted || scorePending) {
      return (
        <div className="flex flex-col gap-[18px]">
          <ProfileIdentity model={model} actions={phoneBack} />
          <InsightBox compact title={notCompleted ? "Profile incomplete" : "Score pending"}>
            {notCompleted
              ? "This athlete must complete their profile setup."
              : "The CacheScore™ is being calculated. Please check back shortly."}
          </InsightBox>
        </div>
      );
    }
    return (
      <>
        <SubscriptionStatusModal isOpen={showSubscription} onClose={closeSubscription} />
        <ProfileBody
          model={model}
          athlete={athlete}
          cacheInformation={cacheInformation}
          matches={matches}
          premium={VIEWER_PREMIUM}
          selfView={false}
          overviewHeader={<ProfileIdentity model={model} subtitle={profileSubtitle(model, athlete, cacheInformation)} actions={phoneBack} />}
        />
      </>
    );
  }

  if (!athlete.email) {
    return (
      <div className="flex flex-col pb-10">
        <PageHeader eyebrow={eyebrow} title="Athlete not found" actions={back} />
        <EmptyState>We couldn't locate this athlete's profile.</EmptyState>
      </div>
    );
  }

  if (notCompleted || scorePending) {
    return (
      <div className="flex flex-col pb-10">
        <PageHeader eyebrow={eyebrow} title={model.fullName} actions={back} />
        <InsightBox title={notCompleted ? "Profile incomplete" : "Score pending"} className="max-w-[640px]">
          {notCompleted
            ? "This athlete must complete their profile setup."
            : "The CacheScore™ is being calculated. Please check back shortly."}
        </InsightBox>
      </div>
    );
  }

  // The CacheValue card's description offers the CacheValue in context
  // modal, which shows the valuation, so only to a viewer the Paywall lets
  // through, and only on a profile with that context (none for a
  // PerformanceValue profile or one with no valuation yet; D3).
  const canSeeValue = (entitlement === "admin" || VIEWER_PREMIUM.includes(entitlement)) && Boolean(model.context);

  return (
    <div>
      <SubscriptionStatusModal isOpen={showSubscription} onClose={closeSubscription} />
      <PageHeader
        eyebrow={eyebrow}
        title={model.fullName}
        subtitle={profileSubtitle(model, athlete, cacheInformation)}
        actions={
          <>
            <SocialLinks links={model.socialMedia} />
            {back}
          </>
        }
      />

      <ProfileBody
        model={model}
        athlete={athlete}
        cacheInformation={cacheInformation}
        matches={matches}
        premium={VIEWER_PREMIUM}
        selfView={false}
        onInfo={canSeeValue ? () => setContextOpen(true) : null}
        tab={tab}
        onTabChange={setTab}
      />

      {contextOpen && canSeeValue && <ValueContextModal model={model} selfView={false} onClose={() => setContextOpen(false)} />}
    </div>
  );
};

export default AthleteProfileView;
